<template>
    <v-container class="px-0" fluid>
        <v-card class="card-style px-1" outlined elevation="2">
            <v-card-title class="d-flex flex-nowrap pa-0 justify-center align-center">
                <!-- Título de la sección -->
                <p class="text-h5 text-truncate mb-5 mr-1">{{ $t('closedSignals.title') }}</p>
                <!-- Ícono de ayuda -->
                <v-tooltip bottom open-on-click max-width="180">
                    <template v-slot:activator="{ on }">
                        <div v-on="on" icon color="grey" class="mb-5 mr-2" size="10">
                            <v-icon size="15">mdi-help-circle</v-icon>
                        </div>
                    </template>
                    <span style="justify-content: center !important">{{ $t('closedSignals.help') }}</span>
                </v-tooltip>
                <!-- Botón para mostrar/ocultar los filtros -->
                <v-btn
                    v-if="!loadingClosedSignals"
                    class="mb-5"
                    icon
                    @click="toggleFilters">
                    <v-icon>{{ filtersVisible ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
            </v-card-title>

            <!-- Filtros -->
            <v-slide-y-transition v-if="!loadingClosedSignals">
                <v-card-subtitle v-if="filtersVisible" class="elevation-2 mx-4 mt-2">
                    <v-row class="pt-2">
                        <v-col cols="12" md="4" class="pb-0">
                            <v-text-field
                                v-model="filters.tickerName"
                                :label="$t('closedSignals.filter.tickerName')"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4" class="pb-0">
                            <v-select
                                v-model="filters.sector"
                                :items="getSectors"
                                :label="$t('closedSignals.filter.sector')"
                                outlined
                                dense
                            ></v-select>
                        </v-col>
                        <v-col cols="12" md="4" class="py-0">
                            <v-dialog ref="dialogSignals" v-model="calendarModalSignal" :return-value.sync="filters.rangeSignalDates" persistent width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field 
                                        v-model="filters.rangeSignalDatesFormatted" 
                                        :label="$t('closedSignals.filter.signalDate')" 
                                        @blur="filters.rangeSignalDates = datePickerparseDate(filters.rangeSignalDatesFormatted)" 
                                        prepend-icon="mdi-calendar" 
                                        v-bind="attrs"
                                        v-on="on">
                                    </v-text-field>
                                </template>
                                <v-date-picker v-model="filters.rangeSignalDates" :min="filters.minDate" scrollable range>
                                    <v-spacer></v-spacer>
                                    <v-btn text color="primary" @click="calendarModalSignal = false"> {{ $t('general.cancel') }} </v-btn>
                                    <v-btn text color="primary" @click="$refs.dialogSignals.save([])"> {{ $t('general.clear') }} </v-btn>
                                    <v-btn text color="primary" @click="$refs.dialogSignals.save(filters.rangeSignalDates)"> {{ $t('general.ok') }} </v-btn>
                                </v-date-picker>
                            </v-dialog>
                        </v-col>
                        <v-col cols="12" md="4" class="pb-0">
                            <v-range-slider v-model="filters.signalPriceRange" :min="getMinPriceAvailable" :max="getMaxPriceAvailable" :step="1" strict thumb-label :label="$t('closedSignals.filter.signalPrice')">
                                <template v-slot:thumb-label="{ value }">
                                    <span>{{ formatNumber(value, false) }}</span>
                                </template>
                            </v-range-slider>
                        </v-col>
                        <v-col cols="12" md="4" class="pb-0">
                            <v-range-slider v-model="filters.signalVolumeRange" :min="getMinVolumeAvailable" :max="getMaxVolumeAvailable" :step="1" strict thumb-label :label="$t('closedSignals.filter.signalVolume')">
                                <template v-slot:thumb-label="{ value }">
                                    <span>{{ formatLargeNumber(value) }}</span>
                                </template>
                            </v-range-slider>
                        </v-col>
                        <v-col cols="12" md="4" class="py-0">
                            <v-dialog ref="dialogFinish" v-model="calendarModalFinish" :return-value.sync="filters.rangeFinishedDates" persistent width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field 
                                        v-model="filters.rangeFinishedDatesFormatted" 
                                        :label="$t('closedSignals.filter.finishDate')" 
                                        @blur="filters.rangeFinishedDates = datePickerparseDate(filters.rangeFinishedDatesFormatted)" 
                                        prepend-icon="mdi-calendar" 
                                        v-bind="attrs"
                                        v-on="on">
                                    </v-text-field>
                                </template>
                                <v-date-picker v-model="filters.rangeFinishedDates" :min="filters.minDate" scrollable range>
                                    <v-spacer></v-spacer>
                                    <v-btn text color="primary" @click="calendarModalFinish = false"> {{ $t('general.cancel') }} </v-btn>
                                    <v-btn text color="primary" @click="$refs.dialogFinish.save([])"> {{ $t('general.clear') }} </v-btn>
                                    <v-btn text color="primary" @click="$refs.dialogFinish.save(filters.rangeFinishedDates)"> {{ $t('general.ok') }} </v-btn>
                                </v-date-picker>
                            </v-dialog>
                        </v-col>
                    </v-row>
                </v-card-subtitle>
            </v-slide-y-transition>

            <!-- Tabla -->
            <v-skeleton-loader
                v-if="loadingClosedSignals"
                type="image"
                class="height-450"
            ></v-skeleton-loader>
            <v-card-text v-else class="mt-4">
                <v-data-table
                    :headers="headers"
                    :items="filteredItems"
                    :sort-by.sync="filters.sortBy"
                    :sort-desc.sync="filters.sortDesc"
                    :footer-props="{
                        'items-per-page-options': [5, 10, 20, 50],
                        'items-per-page-text': $t('closedSignals.table.footer.pageable')
                    }"
                    :items-per-page="(isMobileDevice() ? 5 : 10)"
                    class="material-table elevation-1"
                    dense
                    item-value="id"
                    :no-data-text="$t('closedSignals.table.empty')"
                    :custom-sort="tableSortConfiguration" >

                    <template v-slot:item.ticker="{ item }">
                        <a @click="pushSignalDetailPage(item.id)">{{ item.ticker }}</a>
                    </template>
                    <template v-slot:item.signalDate="{ item }">
                        <span>{{ formatDate(item.signalDate) }}</span>
                    </template>
                    <template v-slot:item.finishDate="{ item }">
                        <span>{{ formatDate(item.finishDate) }}</span>
                    </template>
                    <template v-slot:item.volume="{ item }">
                        <div class="same-line-flex justify-center">
                            <span class="min-width-50">{{ formatNumber(calculateVolumeCompletion(item.breakoutVolume, item.signalVolume), true) }}%</span>
                            <v-progress-circular
                                :key="'vol-'+item.id"
                                :value="calculateVolumeCompletion(item.breakoutVolume, item.signalVolume)"
                                color="primary"
                                class="ml-2"
                            ></v-progress-circular>
                        </div>
                    </template>
                    <template v-slot:item.percentageGain="{ item }">
                        <div v-if="item.price >= item.signalPrice" class="text-center text-no-wrap">
                            {{calculateGrowthPercentageFormatted(item.price, item.signalPrice)}}% <v-icon color="success">mdi-arrow-top-right</v-icon>
                        </div>
                        <div v-else class="text-center text-no-wrap">
                            {{calculateGrowthPercentageFormatted(item.price, item.signalPrice)}}% <v-icon color="error">mdi-arrow-bottom-right</v-icon>
                        </div>
                    </template>
                    <template v-slot:item.technical="{ item }">
                        <div class="my-2">
                            <v-chip link :color="getCustomIndicatorsColor(item.indicators.technical)">
                                {{item.indicators.technical}}%
                            </v-chip>
                          </div>
                    </template>
                    <template v-slot:item.fundamental="{ item }">
                        <div class="my-2">
                            <v-chip link :color="getCustomIndicatorsColor(item.indicators.fundamental)">
                                {{item.indicators.fundamental}}%
                            </v-chip>
                          </div>
                    </template>
                    <template v-slot:item.strength="{ item }">
                        <div class="my-2">
                            <v-chip link :color="getCustomIndicatorsColor(item.indicators.strength)">
                                {{item.indicators.strength}}%
                            </v-chip>
                          </div>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
        <BasicModal :modalCode="modalCode"></BasicModal>
    </v-container>
  </template>

<script>
    import axios from 'axios';

    import { UtilsMixin } from '@/mixins/utils';

    import BasicModal from '@/components/Modal/BasicModal';

    export default {
        name: 'ClosedSignals',
        mixins: [ 
            UtilsMixin 
        ],
        components: {
            BasicModal
        },
        data() {
            return {
                // Filtros
                filtersVisible: false,
                // Datos de la tabla
                headers: [
                    { text: this.$t('closedSignals.table.ticker'), value: "ticker" },
                    { text: this.$t('closedSignals.table.sector'), value: "sector" },
                    { text: this.$t('closedSignals.table.signalDate'), value: "signalDate" },
                    { text: this.$t('closedSignals.table.finishDate'), value: "finishDate" },
                    { text: this.$t('closedSignals.table.percentageGain'), value: "percentageGain", align: 'center' },
                    { text: this.$t('closedSignals.table.signalVolume'), value: "volume", align: 'center' },
                    { text: this.$t('closedSignals.table.technical'), value: "technical", align: 'center' },
                    { text: this.$t('closedSignals.table.fundamental'), value: "fundamental", align: 'center' },
                    { text: this.$t('closedSignals.table.strength'), value: "strength", align: 'center' },
                ],
                calendarModalSignal: false,
                calendarModalFinish: false,
                loadingClosedSignals: true,
                modalCode: '',
                closedSignalsItems: []
            };
        },
        computed: {
            filters: {
                get() {
                    return this.$store.getters["pageFilters/getObject"]("closedSignalsFilters");
                },
                set(newValue) {
                    this.$store.commit("pageFilters/SET_OBJECT", { objectKey: "closedSignalsFilters", newObject: newValue });
                },
            },
            getSectors() {
                if (this.closedSignalsItems.length == 0) return [];

                return this.filters.sector ? ["", ...new Set(this.closedSignalsItems.map(s => s.sector))].sort()
                        : [...new Set(this.closedSignalsItems.map(s => s.sector))].sort();
            },
            getMaxPriceAvailable() {
                if (this.closedSignalsItems.length == 0) return 100;

                let value = this.closedSignalsItems.reduce((max, item) => {
                    return item.signalPrice > max ? item.signalPrice : max;
                }, 0);
                return Math.round(value + 1);
            },
            getMinPriceAvailable() {
                if (this.closedSignalsItems.length == 0) return 0;

                let value = this.closedSignalsItems.reduce((min, item) => {
                    return item.signalPrice < min ? item.signalPrice : min;
                }, Infinity);
                return Math.round(value - 1);
            },
            getMaxVolumeAvailable() {
                if (this.closedSignalsItems.length == 0) return 100000;

                let value = this.closedSignalsItems.reduce((max, item) => {
                    return item.signalVolume > max ? item.signalVolume : max;
                }, 0);
                return value + 1;
            },
            getMinVolumeAvailable() {
                if (this.closedSignalsItems.length == 0) return 0;

                let value = this.closedSignalsItems.reduce((min, item) => {
                    return item.signalVolume < min ? item.signalVolume : min;
                }, Infinity);
                return value - 1;
            },
            filteredItems() {
                return this.closedSignalsItems.filter(item => 
                    // Filtro por ticker y nombre
                    (!this.filters.tickerName || (item.ticker.toLowerCase().includes(this.filters.tickerName.toLowerCase()) || item.name.toLowerCase().includes(this.filters.tickerName.toLowerCase())))
                    // Filtro por sector
                    && (!this.filters.sector || (item.sector.toLowerCase().includes(this.filters.sector.toLowerCase()) ))
                    // Filtro por precio de la señal
                    && (item.signalPrice >= this.filters.signalPriceRange[0] && item.signalPrice <= this.filters.signalPriceRange[1])
                    // Filtro por volumen de la señal
                    && (item.signalVolume >= this.filters.signalVolumeRange[0] && item.signalVolume <= this.filters.signalVolumeRange[1])
                    // Filtro por rango de fechas (creación de señal)
                    && ((!this.filters.rangeSignalDates || this.filters.rangeSignalDates.length == 0)
                        || (this.filters.rangeSignalDates.length == 1 && new Date(this.filters.rangeSignalDates[0]).getTime() == new Date(item.signalDate).getTime())
                        || (this.filters.rangeSignalDates.length == 2 && new Date(this.filters.rangeSignalDates[0]).getTime() <= new Date(item.signalDate).getTime() && new Date(this.filters.rangeSignalDates[1]).getTime() >= new Date(item.signalDate).getTime())
                    )
                    // Filtro por rango de fechas (finalización de señal)
                    && ((!this.filters.rangeFinishedDates || this.filters.rangeFinishedDates.length == 0)
                        || (this.filters.rangeFinishedDates.length == 1 && new Date(this.filters.rangeFinishedDates[0]).getTime() == new Date(item.finishDate).getTime())
                        || (this.filters.rangeFinishedDates.length == 2 && new Date(this.filters.rangeFinishedDates[0]).getTime() <= new Date(item.finishDate).getTime() && new Date(this.filters.rangeFinishedDates[1]).getTime() >= new Date(item.finishDate).getTime())
                    )
                );
            }
        },
        watch: {
            'filters.rangeSignalDates' () {
                this.filters.rangeSignalDatesFormatted = this.datePickerformatDate(this.filters.rangeSignalDates);
                this.filters.minDate = this.filters.rangeSignalDates && this.filters.rangeSignalDates.length == 1 ? this.datePickerparseDate(this.filters.rangeSignalDatesFormatted)[0] : null;
            },
            'filters.rangeFinishedDates' () {
                this.filters.rangeFinishedDatesFormatted = this.datePickerformatDate(this.filters.rangeFinishedDates);
                this.filters.minDate = this.filters.rangeFinishedDates && this.filters.rangeFinishedDates.length == 1 ? this.datePickerparseDate(this.filters.rangeFinishedDatesFormatted)[0] : null;
            },
        },
        created() {
            // Se muestran los filtros si se accede desde desktop
            this.filtersVisible = this.isMobileDevice() ? false : true;
        },
        mounted() {
            // API calls
            this.getClosedSignals();
        },
        methods: {
            // ---- FUNCIONES UTILIZADAS POR LOS COMPONENTES EN PANTALLA  ----
            // Permite mostrar u ocultar las opciones de filtrado
            toggleFilters() {
                this.filtersVisible = !this.filtersVisible;
            },
            // Control para ordenar los resultados en la tabla de señales
            tableSortConfiguration(items, sortBy, descending) {
                if (sortBy.length > 0) {
                    if (sortBy[0] === "volume") {
                        items.sort((a, b) => {
                            const scoreA = this.calculateVolumeCompletion(a.breakoutVolume, a.signalVolume)
                            const scoreB = this.calculateVolumeCompletion(b.breakoutVolume, b.signalVolume)

                            if (typeof scoreA === "number" && typeof scoreB === "number") {
                                return descending[0] ? scoreB - scoreA : scoreA - scoreB;
                            } else {
                                return descending[0] ? scoreB.toString().localeCompare(scoreA.toString()) : scoreA.toString().localeCompare(scoreB.toString());
                            }
                        });
                    } else if (sortBy[0] === "percentageGain") {
                        items.sort((a, b) => {
                            const scoreA = this.calculateGrowthPercentage(a.price, a.signalPrice)
                            const scoreB = this.calculateGrowthPercentage(b.price, b.signalPrice)

                            if (typeof scoreA === "number" && typeof scoreB === "number") {
                                return descending[0] ? scoreB - scoreA : scoreA - scoreB;
                            } else {
                                return descending[0] ? scoreB.toString().localeCompare(scoreA.toString()) : scoreA.toString().localeCompare(scoreB.toString());
                            }
                        });
                    } else if (sortBy[0] === "technical" || sortBy[0] === "fundamental" || sortBy[0] === "strength") {
                        items.sort((a, b) => {
                            const scoreA = a["indicators"][sortBy[0]]
                            const scoreB = b["indicators"][sortBy[0]]

                            return descending[0] ? scoreB - scoreA : scoreA - scoreB;
                        });
                    } else {
                        items.sort((a, b) => {
                            const valA = a[sortBy[0]];
                            const valB = b[sortBy[0]];

                            if (typeof valA === "number" && typeof valB === "number") {
                                return descending[0] ? valB - valA : valA - valB;
                            } else {
                                return descending[0] ? valB.toString().localeCompare(valA.toString()) : valA.toString().localeCompare(valB.toString());
                            }
                        });
                    }
                }
                return items;
            },
            // Acceso al detalle de una señal
            pushSignalDetailPage(id) {
                this.$router.push({ name: 'SignalDetail', params: { id: id } });
            },
            // Configura los máximos en los filtros de rangos
            setupMaxAndMinFilters() {
                // Se inicializan los filtros de rangos con todos los valores asignados
                if (this.filters.signalPriceRange.length == 0 && this.filters.signalVolumeRange.length == 0) {
                    this.filters.signalPriceRange = [this.getMinPriceAvailable, this.getMaxPriceAvailable];
                    this.filters.signalVolumeRange = [this.getMinVolumeAvailable, this.getMaxVolumeAvailable];
                }
            },
            showErrorModal() {
                this.modalCode = 'apiErrorModal';
                this.showDefaultErrorModal(this.modalCode);
            },
            // ---- PETICIONES AL BACKEND ----
            // Consulta el listado de señales cerradas recientemente
            async getClosedSignals() {
                this.loadingClosedSignals = true;
                try {
                    const response = await axios.get('/signal/getClosedSignals');
                    this.closedSignalsItems = response.data;
                    this.setupMaxAndMinFilters();
                } catch (e) {
                    this.showErrorModal();
                } finally {
                    this.loadingClosedSignals = false;
                }
            },
        },
    };
</script>

<style scoped>
</style>